import { api } from '../../services/api'
import { showError, showSuccess } from '../misc/misc.actions'

export const processFailedChargesForHousehold =
	({ householdId, failedChargesTotalInCents }) =>
	async (dispatch) => {
		dispatch({ type: 'UPDATE_HOUSEHOLD' })
		try {
			const {
				data: { data: apiData, errors: apiErrors }
			} = await api.post(`/clients/${householdId}/charges/process/failed`, {
				failedChargesTotalInCents
			})

			if (apiErrors.length > 0) {
				dispatch(showError('Error processing failed charges.'))
				dispatch({ type: 'UPDATE_HOUSEHOLD_FAIL', payload: apiErrors[0] })
				return
			}

			const [successfulCharges, failedCharges] = apiData

			if (failedCharges.length > 0 && successfulCharges.length === 0) {
				const failMessage = `${failedCharges.length} charge(s) failed. No charges were successful.`
				dispatch(showError(failMessage))
				dispatch({ type: 'UPDATE_HOUSEHOLD_FAIL', payload: failMessage })
			}

			if (failedCharges.length > 0 && successfulCharges.length > 0) {
				const mixedMessage = `${failedCharges.length} charge(s) failed. ${successfulCharges.length} charge(s) succeeded.`
				dispatch(showError(mixedMessage))
				dispatch({ type: 'UPDATE_HOUSEHOLD_FAIL', payload: mixedMessage })
			}

			if (successfulCharges.length > 0 && failedCharges.length === 0) {
				dispatch(showSuccess(`${successfulCharges.length} failed charge(s) successfully updated`))
			}

			dispatch(getTransactions(householdId))
		} catch (e) {
			dispatch(showError('Error processing failed charges.'))
			dispatch({ type: 'UPDATE_HOUSEHOLD_FAIL', payload: e.toString() })
		}
	}

export const createHousehold = (household, history) => {
	return (dispatch) => {
		dispatch({ type: 'POST_HOUSEHOLD' })

		return api
			.post(`/clients`, household)
			.then((response) => {
				const { accounts } = response.data

				dispatch({ type: 'SET_ACCOUNTS', payload: accounts })
				dispatch({ type: 'SET_SELECTED_HOUSEHOLD', payload: response.data })

				dispatch(showSuccess('Client created successfully.'))

				if (response.data && response.data._id && history) {
					setTimeout(() => {
						history.push(`/clients/${response.data._id}/contacts`)
					}, 500)
				}
			})
			.catch((error) => {
				if (error?.data?.message) {
					dispatch(showError(error.data.message))
					const { response } = error

					dispatch({
						type: 'POST_HOUSEHOLD_FAIL',
						payload: response && response.data ? response.data : null
					})
				} else {
					dispatch(showError('An error occured while creating a client.'))
					const { response } = error
					dispatch({
						type: 'POST_HOUSEHOLD_FAIL',
						payload: response && response.data ? response.data : null
					})
				}
			})
	}
}

export const setSelectedLineItemFilters = (filters) => {
	return (dispatch) => {
		dispatch({ type: 'SET_SELECTED_LINE_ITEM_FILTERS', payload: filters })
	}
}

/**
 * This function calls the /clients/search/advanced endpoint to search for households.
 * It was created to allow for a more granular search that can help minimize the payload
 * If leanPayload is false, the payload will include the household's pets, memberships, accounts, and credits and all household fields
 *
 * @param {*} dispatch - redux dispatch function
 * @param {{
 * 	marketId: string,
 * 	search: string,
 * 	searchBy: 'contact' | 'pet_name' | 'email' | 'phone'
 * 	leanPayload: boolean,
 * 	includePets: boolean,
 * 	includeMemberships: boolean,
 * 	includeAccounts: boolean,
 * 	includeCredits: boolean
 * 	includeGroomingCredits:  boolean
 * }} param1
 * @param {} param1.marketId The market id of the current facility
 * @param {} param1.search The search text - Length must be at least 3 characters
 * @param {} param1.searchBy The field to search by
 * @param {} param1.leanPayload If true, minimal fields are returned
 * @param {} param1.includePets If true, the payload will include the household's pets
 * @param {} param1.includeMemberships If true, the payload will include the household's memberships
 * @param {} param1.includeAccounts If true, the payload will include the household's accounts
 * @param {} param1.includeCredits If true, the payload will include all household credits
 * @param {} param1.includeGroomingCredits If true, then it will only include the household's grooming credits
 * @returns
 */
export const getHouseholdsWithAdvancedSearch = async (
	dispatch,
	{
		marketId,
		search = '',
		searchBy,
		leanPayload = false,
		includePets = false,
		includeMemberships = false,
		includeAccounts = false,
		includeCredits = false,
		includeGroomingCredits = false
	}
) => {
	if (search.length < 3) {
		return
	}

	dispatch({ type: 'GET_HOUSEHOLDS' })

	try {
		let url = `/clients/search/advanced?leanPayload=${leanPayload}`

		if (marketId) {
			url += `&marketId=${marketId}`
		}

		if (search) {
			url += `&text=${search}`
		}

		if (searchBy) {
			url += `&searchBy=${searchBy}`
		}

		if (includeMemberships) {
			url += `&includeMemberships=${includeMemberships}`
		}

		if (includeAccounts) {
			url += `&includeAccounts=${includeAccounts}`
		}

		if (includePets) {
			url += `&includePets=${includePets}&includeVaccines=true`
		}

		if (includeCredits) {
			url += `&includeCredits=${includeCredits}`
		}

		if (includeGroomingCredits && !includeCredits) {
			url += `&includeGroomingCredits=${includeGroomingCredits}`
		}

		const response = await api.get(url)
		const { data, errors, messages } = response.data

		if (errors && errors.length > 0) {
			dispatch({ type: 'GET_HOUSEHOLDS_FAIL' })
			dispatch(showError(errors[0].message))
			return
		}
		if (messages && messages.length > 0) {
			dispatch(showSuccess(messages[0].message))
			return
		}

		dispatch({ type: 'GET_HOUSEHOLDS_SUCCESS', payload: data })
	} catch (e) {
		dispatch({ type: 'GET_HOUSEHOLDS_FAIL' })
	}
}

export const getHouseholds = (marketId, search, searchBy, page) => {
	return (dispatch) => {
		dispatch({ type: 'GET_HOUSEHOLDS' })

		return api
			.get(
				`/markets/${marketId}/clients${search ? `?text=${search}` : ''}${
					searchBy ? `&searchBy=${searchBy}` : ''
				}${page ? `&page=${page}` : ''}`
			)
			.then((response) => {
				dispatch({ type: 'GET_HOUSEHOLDS_SUCCESS', payload: response.data })
			})
			.catch(() => {
				dispatch({ type: 'GET_HOUSEHOLDS_FAIL' })
			})
	}
}

export const getHouseholdByIdAsync = async (dispatch, householdId) => {
	dispatch({ type: 'GET_HOUSEHOLDS' })
	try {
		const response = await api.get(`/clients/${householdId}`)
		const { accounts } = response.data

		dispatch({ type: 'SET_ACCOUNTS', payload: accounts })
		dispatch({ type: 'SET_SELECTED_HOUSEHOLD', payload: response.data })
	} catch (e) {
		dispatch({ type: 'GET_HOUSEHOLDS_FAIL' })
	}
}
export const getHouseholdById = (householdId) => {
	return (dispatch) => {
		dispatch({ type: 'GET_HOUSEHOLDS' })

		return api
			.get(`/clients/${householdId}`)
			.then((response) => {
				const { accounts } = response.data

				dispatch({ type: 'SET_ACCOUNTS', payload: accounts })
				dispatch({ type: 'SET_SELECTED_HOUSEHOLD', payload: response.data })
			})
			.catch(() => {
				dispatch({ type: 'GET_HOUSEHOLDS_FAIL' })
			})
	}
}

export const getServiceData = (householdId) => {
	return (dispatch) => {
		dispatch({ type: 'GET_HOUSEHOLD' })

		return api
			.get(`/clients/${householdId}/service/data`)
			.then((response) => {
				dispatch({ type: 'UPDATE_HOUSEHOLD_SUCCESS', payload: response.data })
			})
			.catch(() => {
				dispatch({ type: 'GET_HOUSEHOLDS_FAIL' })
			})
	}
}

export const getMembershipTypes = (params = {}) => {
	return (dispatch) => {
		dispatch({ type: 'GET_MEMBERSHIP_TYPES' })

		const query = {}
		if (params.facilityId) query.facility_id = params.facilityId
		if (params.householdId) query.household_id = params.householdId

		return api
			.get(`/membershipTypes`, { params: query })
			.then((response) => {
				dispatch({ type: 'SET_MEMBERSHIP_TYPES', payload: response.data })
			})
			.catch(() => {
				dispatch({ type: 'GET_MEMBERSHIP_TYPES_FAIL' })
			})
	}
}

export const addMembership = (membership) => {
	return (dispatch) => {
		dispatch({ type: 'POST_MEMBERSHIP' })

		return api
			.post(`/membership`, membership)
			.then((response) => {
				dispatch(getHouseholdById(membership.household_id))
				dispatch(showSuccess('Membership added successfully.'))
			})
			.catch((error) => {
				const { response } = error
				dispatch(
					showError(
						'An error occured while adding membership. ' +
							(response && response.data.message ? response.data.message : '')
					)
				)
				dispatch({
					type: 'POST_MEMBERSHIP_FAIL',
					payload: response && response.message ? response.message : null
				})
			})
	}
}

export const updateMembership = (membership) => {
	return (dispatch) => {
		dispatch({ type: 'UPDATE_MEMBERSHIP' })

		return api
			.patch(`/updateMembership`, membership)
			.then((response) => {
				dispatch(getHouseholdById(membership.household_id))
				dispatch(showSuccess('Membership updated successfully.'))
			})
			.catch((error) => {
				dispatch(showError('An error occured while updating membership.'))
				const { response } = error
				console.log(error)
				dispatch({
					type: 'UPDATE_MEMBERSHIP_FAIL',
					payload: response && response.data ? response.data : null
				})
			})
	}
}

export const addAdditionalDogs = (membership, membershipID) => {
	return (dispatch) => {
		dispatch({ type: 'UPDATE_MEMBERSHIP' })

		console.log(membership, membershipID)

		return api
			.patch(`/membership/${membershipID}/addDogs`, membership)
			.then((response) => {
				dispatch(
					showSuccess(
						membership.additional_dogs >= 1
							? 'Added dogs to the membership successfully.'
							: 'Dogs removed successfully'
					)
				)
				// race condition here, this is super hacky, don't judge me
				setTimeout(() => {
					dispatch(getHouseholdById(membership.household_id))
				}, 500)
			})
			.catch((error) => {
				dispatch(showError('An error occured while adding/removing dogs to membership.'))
				const { response } = error
				console.log(error)
				dispatch({
					type: 'UPDATE_MEMBERSHIP_FAIL',
					payload: response && response.data ? response.data : null
				})
			})
	}
}

export const updateHousehold = (householdId, household) => {
	return (dispatch) => {
		dispatch({ type: 'UPDATE_HOUSEHOLD' })
		return api
			.patch(`/clients/${householdId}`, household)
			.then((response) => {
				const { accounts } = response.data
				dispatch({ type: 'SET_ACCOUNTS', payload: accounts })
				dispatch({ type: 'SET_SELECTED_HOUSEHOLD', payload: response.data })
				dispatch({ type: 'UPDATE_HOUSEHOLD_SUCCESS', payload: response.data })
			})
			.catch(() => {
				dispatch({ type: 'UPDATE_HOUSEHOLD_FAIL' })
			})
	}
}

export const updateHouseholdFoundingTag = (householdId, household) => {
	return (dispatch) => {
		dispatch({ type: 'UPDATE_HOUSEHOLD' })

		return api
			.put(`/clients/foundingTag/${householdId}`, household)
			.then((response) => {
				dispatch({ type: 'UPDATE_FOUNDING', payload: household.founding_member })
			})
			.catch(() => {
				dispatch({ type: 'UPDATE_HOUSEHOLD_FAIL' })
			})
	}
}

export const updateMuteRenewalAlert = (householdId, household) => {
	return (dispatch) => {
		dispatch({ type: 'UPDATE_HOUSEHOLD' })

		return api
			.put(`/clients/muteRenewalAlert/${householdId}`, household)
			.then((_response) => {
				dispatch({ type: 'UPDATE_MUTE_RENEWAL_ALERT', payload: household.mute_renewal_alert })
			})
			.catch(() => {
				dispatch({ type: 'UPDATE_HOUSEHOLD_FAIL' })
			})
	}
}

export const getBalances = (householdId) => {
	return (dispatch) => {
		dispatch({ type: 'GET_HOUSEHOLD' })

		return api
			.get(`/clients/${householdId}/balance`)
			.then((response) => {
				dispatch({ type: 'UPDATE_HOUSEHOLD_SUCCESS', payload: response.data })
			})
			.catch(() => {
				dispatch({ type: 'UPDATE_HOUSEHOLD_FAIL' })
			})
	}
}

export const getTransactions = (householdId) => {
	return (dispatch) => {
		dispatch({ type: 'GET_HOUSEHOLD' })

		return api
			.get(`/clients/${householdId}/transactions`)
			.then((response) => {
				dispatch({ type: 'UPDATE_HOUSEHOLD_SUCCESS', payload: response.data })
			})
			.catch(() => {
				dispatch({ type: 'UPDATE_HOUSEHOLD_FAIL' })
			})
	}
}

export const setPaymentMethod = (householdId, token, facilityId) => {
	return (dispatch) => {
		dispatch({ type: 'UPDATE_HOUSEHOLD' })

		return api
			.post(`/clients/${householdId}/payment/method`, {
				...token,
				facility_id: facilityId
			})
			.then((response) => {
				dispatch(showSuccess('Payment method updated successfully.'))
				dispatch({ type: 'UPDATE_HOUSEHOLD_SUCCESS', payload: response.data })
				return true
			})
			.catch((e) => {
				const message = e?.data?.message || 'An error occured while updating the payment method.'
				dispatch({
					type: 'UPDATE_HOUSEHOLD_FAIL',
					payload: message
				})
				return false
			})
	}
}
